// src/components/layout/Navbar.js
import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth.js';
import { useCompany } from '../../hooks/useCompany.js';
import { useTheme } from '../../hooks/useTheme.js';
import LoginForm from '../auth/LoginForm.js';
import { isDrawingRoute, subscribeFullBleed } from './FullBleedLayout.js';
import affiliateService from '../../services/affiliateService.js';

// ============================================================================
// NAVBAR LOGO — pick 'blue' or 'black'
// ============================================================================
const NAVBAR_LOGO = 'blue';

// Files live in /public, so they are served from the site root.
//   blue   reads on both the white and the dark (slate-800) navbar as-is.
//   black  would vanish on the dark navbar, so in dark mode it is inverted to
//          white with Tailwind's dark:invert.
const LOGO_VARIANTS = {
  blue:  { src: '/quickbid-logo-blue.png',  darkClass: '' },
  black: { src: '/quickbid-logo-black.png', darkClass: 'dark:invert' },
};
const logo = LOGO_VARIANTS[NAVBAR_LOGO] || LOGO_VARIANTS.blue;

// Theme toggle button component
const ThemeToggle = () => {
  const { theme, toggleTheme } = useTheme();
  
  return (
    <button
      onClick={toggleTheme}
      className="p-2 rounded-md text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors"
      aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
    >
      {theme === 'dark' ? (
        // Sun icon for dark mode (click to switch to light)
        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z"
            clipRule="evenodd"
          />
        </svg>
      ) : (
        // Moon icon for light mode (click to switch to dark)
        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
          <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
        </svg>
      )}
    </button>
  );
};

const Navbar = () => {
  const navigate = useNavigate();
  const { currentUser, isAuthenticated, logout, updateLogoutSettings, isSuperAdmin, isImpersonating } = useAuth();
  const { userCompanies, selectedCompany, changeSelectedCompany } = useCompany();
  const location = useLocation();
  
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showSecurityDropdown, setShowSecurityDropdown] = useState(false);
  const [showEditLogoutForm, setShowEditLogoutForm] = useState(false);
  const [showSuperAdminDropdown, setShowSuperAdminDropdown] = useState(false);
  const [showMoreDropdown, setShowMoreDropdown] = useState(false);

  /**
   * Is this user already an affiliate?
   *
   * Only changes the LABEL. The link is shown to everyone either way, because
   * /affiliate is where someone discovers the program and applies — hiding it
   * from non-affiliates would mean nobody could ever find it.
   *
   * Sits in the user dropdown rather than the top-level nav on purpose: a
   * cabinet maker reads that page once and never returns, and a permanent
   * top-level link to something irrelevant is clutter for the majority.
   */
  const [isAffiliate, setIsAffiliate] = useState(false);

  // One tiny call on login. Fails silently — the navbar is on every page and
  // must never be able to break because an affiliate endpoint is unhappy. A
  // failure simply shows the generic label.
  useEffect(() => {
    if (!isAuthenticated) { setIsAffiliate(false); return; }
    let cancelled = false;
    affiliateService
      .getMyAffiliateDashboard()
      .then((res) => { if (!cancelled) setIsAffiliate(Boolean(res?.affiliate)); })
      .catch(() => { if (!cancelled) setIsAffiliate(false); });
    return () => { cancelled = true; };
  }, [isAuthenticated]);
  const [logoutDays, setLogoutDays] = useState(
    currentUser ? Math.floor(currentUser.automaticLogoutDurationMinutes / (60 * 24)) : 7
  );
  const [logoutHours, setLogoutHours] = useState(
    currentUser ? Math.floor(currentUser.automaticLogoutDurationMinutes / 60) % 24 : 0
  );
  const [lessThanDayLogout, setLessThanDayLogout] = useState(
    currentUser ? currentUser.lessThanDayLogout : false
  );
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Whether SOME currently-mounted screen has announced itself as full-bleed
  // (via FullBleedLayout). This is the primary signal for the order Drawings
  // workspace, whose URL (/order/:orderId) is shared with the centered
  // pricing workspace — isDrawingRoute() below can't tell those apart from the
  // path alone, but the component that's actually on screen can.
  //
  // useLayoutEffect on both sides (here and inside FullBleedLayout) so this is
  // known before the browser paints — otherwise the navbar would render
  // centered for one frame on a drawing order, then snap to full width.
  const [fullBleedActive, setFullBleedActive] = useState(false);
  useLayoutEffect(() => subscribeFullBleed(setFullBleedActive), []);
  
  const userDropdownRef = useRef(null);
  const securityDropdownRef = useRef(null);
  const editLogoutFormRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const superAdminDropdownRef = useRef(null);
  const moreDropdownRef = useRef(null);

  const isAdmin = currentUser?.employeeTypeId === 1;

  // Full-width whenever we're on a route that's ALWAYS full-bleed (the
  // super-admin drawing-authoring pages), OR whenever whatever screen is
  // currently mounted has told us it's rendering full-bleed (an order that
  // turned out to be a drawing order). Either signal is enough.
  const navFullBleed = isDrawingRoute(location.pathname) || fullBleedActive;

  useEffect(() => {
    if (currentUser) {
      setLogoutDays(Math.floor(currentUser.automaticLogoutDurationMinutes / (60 * 24)));
      setLogoutHours(Math.floor(currentUser.automaticLogoutDurationMinutes / 60) % 24);
      setLessThanDayLogout(currentUser.lessThanDayLogout);
    }
  }, [currentUser]);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (showUserDropdown && userDropdownRef.current && !userDropdownRef.current.contains(event.target)) {
        setShowUserDropdown(false);
      }
      if (showSecurityDropdown && securityDropdownRef.current && !securityDropdownRef.current.contains(event.target)) {
        setShowSecurityDropdown(false);
      }
      if (showEditLogoutForm && editLogoutFormRef.current && !editLogoutFormRef.current.contains(event.target)) {
        setShowEditLogoutForm(false);
      }
      if (showSuperAdminDropdown && superAdminDropdownRef.current && !superAdminDropdownRef.current.contains(event.target)) {
        setShowSuperAdminDropdown(false);
      }
      if (showMoreDropdown && moreDropdownRef.current && !moreDropdownRef.current.contains(event.target)) {
        setShowMoreDropdown(false);
      }
    }
    
    if (showUserDropdown || showSecurityDropdown || showEditLogoutForm || showSuperAdminDropdown || showMoreDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserDropdown, showSecurityDropdown, showEditLogoutForm, showSuperAdminDropdown, showMoreDropdown]);

  const closeAllMenus = () => {
    setShowUserDropdown(false);
    setShowSecurityDropdown(false);
    setShowEditLogoutForm(false);
    setShowSuperAdminDropdown(false);
    setShowMoreDropdown(false);
    setMobileMenuOpen(false);
  };

  const handleLogout = async () => {
    await logout();
    closeAllMenus();
  };

  const toggleLoginForm = () => {
    setShowLoginForm(!showLoginForm);
  };

  const toggleUserDropdown = () => {
    setShowUserDropdown(!showUserDropdown);
    setShowSecurityDropdown(false);
    setShowEditLogoutForm(false);
    setShowSuperAdminDropdown(false);
    setShowMoreDropdown(false);
  };

  const toggleSecurityDropdown = () => {
    setShowSecurityDropdown(!showSecurityDropdown);
    setShowEditLogoutForm(false);
  };

  const toggleEditLogoutForm = () => {
    setShowEditLogoutForm(!showEditLogoutForm);
  };
  
  const closeSecurityDropdown = () => {
    setShowSecurityDropdown(false);
  };

  const toggleSuperAdminDropdown = () => {
    setShowSuperAdminDropdown(!showSuperAdminDropdown);
    setShowUserDropdown(false);
    setShowSecurityDropdown(false);
    setShowEditLogoutForm(false);
    setShowMoreDropdown(false);
  };

  const toggleMoreDropdown = () => {
    setShowMoreDropdown(!showMoreDropdown);
    setShowUserDropdown(false);
    setShowSecurityDropdown(false);
    setShowEditLogoutForm(false);
    setShowSuperAdminDropdown(false);
  };

  const handleSaveLogoutSettings = async () => {
    try {
      if (lessThanDayLogout) {
        if (logoutHours < 1 || logoutHours > 23) {
          alert('Hours must be between 1 and 23');
          return;
        }
      } else {
        if (logoutDays < 1 || logoutDays > 30) {
          alert('Days must be between 1 and 30');
          return;
        }
      }
      
      const durationMinutes = lessThanDayLogout
        ? logoutHours * 60
        : logoutDays * 24 * 60;
      
      await updateLogoutSettings(durationMinutes, lessThanDayLogout);
      setShowEditLogoutForm(false);
    } catch (error) {
      console.error('Failed to update logout settings:', error);
    }
  };

  return (
    <nav className="bg-white dark:bg-slate-800 shadow-md dark:shadow-lg dark:shadow-slate-900/20 relative z-30 transition-colors">
      <div className={`${navFullBleed ? 'w-full' : 'max-w-[1700px] mx-auto'} px-4 sm:px-6`}>
        <div className="flex justify-between h-16">
          <div className="flex">
            <div className="flex-shrink-0 flex items-center">
              <Link to="/" className="flex items-center" aria-label="Quickbid home">
                <img
                  src={logo.src}
                  alt="Quickbid"
                  className={`h-6 sm:h-7 w-auto ${logo.darkClass}`}
                />
              </Link>
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="flex items-center sm:hidden space-x-2">
            <ThemeToggle />
            <button
              type="button"
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
              aria-expanded={mobileMenuOpen}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <span className="sr-only">Open main menu</span>
              {mobileMenuOpen ? (
                <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden sm:flex sm:items-center sm:space-x-2">
            <Link
              to="/pricing"
              className="px-3 py-2 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
            >
              Pricing
            </Link>

            <Link
              to="/about"
              className="px-3 py-2 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
            >
              About
            </Link>

            {/* More dropdown */}
            <div className="relative" ref={moreDropdownRef}>
              <button
                type="button"
                className="flex items-center px-3 py-2 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 focus:outline-none transition-colors"
                onClick={toggleMoreDropdown}
                aria-expanded={showMoreDropdown}
                aria-haspopup="true"
              >
                More
                <svg className="ml-1 h-4 w-4 text-gray-400 dark:text-gray-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>

              {showMoreDropdown && (
                <div className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white dark:bg-slate-800 ring-1 ring-black ring-opacity-5 dark:ring-slate-600 border border-gray-200 dark:border-slate-600 focus:outline-none">
                  <Link
                    to="/terms"
                    onClick={() => setShowMoreDropdown(false)}
                    className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700"
                  >
                    Terms of Service
                  </Link>
                  <Link
                    to="/privacy"
                    onClick={() => setShowMoreDropdown(false)}
                    className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700"
                  >
                    Privacy Policy
                  </Link>
                </div>
              )}
            </div>

            {/* Orders and Admin links - only show if authenticated */}
            {isAuthenticated && (
              <>
                <Link
                  to="/orders"
                  className="px-3 py-2 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
                >
                  Orders
                </Link>

                <Link
                  to="/admin"
                  className="px-3 py-2 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
                >
                  Admin
                </Link>

                {/* Super Admin dropdown */}
                {(isSuperAdmin() || isImpersonating) && (
                  <div className="relative" ref={superAdminDropdownRef}>
                    <button
                      type="button"
                      className="flex items-center px-3 py-2 rounded-md text-sm font-medium text-purple-700 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/30 focus:outline-none transition-colors"
                      onClick={toggleSuperAdminDropdown}
                      aria-expanded={showSuperAdminDropdown}
                      aria-haspopup="true"
                    >
                      {isImpersonating && (
                        <span className="mr-1 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-orange-100 dark:bg-orange-900/50 text-orange-800 dark:text-orange-300">
                          Impersonating
                        </span>
                      )}
                      Super Admin
                      <svg className="ml-1 h-5 w-5 text-purple-400 dark:text-purple-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>

                    {showSuperAdminDropdown && (
                      <div className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white dark:bg-slate-800 ring-1 ring-purple-200 dark:ring-purple-800 border border-purple-200 dark:border-purple-800 focus:outline-none">
                        <Link
                          to="/super-admin/dashboard"
                          onClick={() => setShowSuperAdminDropdown(false)}
                          className="block px-4 py-2 text-sm text-purple-700 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/30"
                        >
                          Dashboard
                        </Link>
                        <Link
                          to="/super-admin/plans"
                          onClick={() => setShowSuperAdminDropdown(false)}
                          className="block px-4 py-2 text-sm text-purple-700 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/30"
                        >
                          Manage Plans
                        </Link>
                        <Link
                          to="/super-admin/companies"
                          onClick={() => setShowSuperAdminDropdown(false)}
                          className="block px-4 py-2 text-sm text-purple-700 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/30"
                        >
                          Manage Companies
                        </Link>
                        <Link
                          to="/super-admin/members"
                          onClick={() => setShowSuperAdminDropdown(false)}
                          className="block px-4 py-2 text-sm text-purple-700 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/30"
                        >
                          Manage Members
                        </Link>
                        <Link
                          to="/super-admin/settings"
                          onClick={() => setShowSuperAdminDropdown(false)}
                          className="block px-4 py-2 text-sm text-purple-700 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/30"
                        >
                          App Settings
                        </Link>
                        <div className="border-t border-purple-100 dark:border-purple-800 my-1"></div>
                        {/* Cabinet drawings authoring (ecard model) */}
                        <Link
                          to="/super-admin/cabinet-drawings"
                          onClick={() => setShowSuperAdminDropdown(false)}
                          className="block px-4 py-2 text-sm text-purple-700 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/30"
                        >
                          Cabinet Drawings
                        </Link>
                        <Link
                          to="/super-admin/item-drawings"
                          onClick={() => setShowSuperAdminDropdown(false)}
                          className="block px-4 py-2 text-sm text-purple-700 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/30"
                        >
                          Item Drawings
                        </Link>
                        <Link
                          to="/super-admin/room-drawings"
                          onClick={() => setShowSuperAdminDropdown(false)}
                          className="block px-4 py-2 text-sm text-purple-700 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/30"
                        >
                          Room Drawings
                        </Link>
                        <Link
                          to="/super-admin/image-library"
                          onClick={() => setShowSuperAdminDropdown(false)}
                          className="block px-4 py-2 text-sm text-purple-700 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/30"
                        >
                          Image Library
                        </Link>
                        <Link
                          to="/super-admin/construction-profiles"
                          onClick={() => setShowSuperAdminDropdown(false)}
                          className="block px-4 py-2 text-sm text-purple-700 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/30"
                        >
                          Construction Profiles
                        </Link>
                        <Link
                          to="/super-admin/engines"
                          onClick={() => setShowSuperAdminDropdown(false)}
                          className="block px-4 py-2 text-sm text-purple-700 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/30"
                        >
                          Engine Parts
                        </Link>
                        <div className="border-t border-purple-100 dark:border-purple-800 my-1"></div>
                        <Link
                          to="/super-admin/impersonation"
                          onClick={() => setShowSuperAdminDropdown(false)}
                          className="block px-4 py-2 text-sm text-purple-700 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/30"
                        >
                          Impersonation
                        </Link>
                      </div>
                    )}
                  </div>
                )}

                {/* User dropdown */}
                <div className="relative" ref={userDropdownRef}>
                  <button
                    type="button"
                    className="flex items-center px-3 py-2 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 focus:outline-none transition-colors"
                    onClick={toggleUserDropdown}
                    aria-expanded={showUserDropdown}
                    aria-haspopup="true"
                  >
                    {currentUser?.firstName || 'User'}
                    <svg className="ml-1 h-5 w-5 text-gray-400 dark:text-gray-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>

                  {showUserDropdown && (
                    <div className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white dark:bg-slate-800 ring-1 ring-black ring-opacity-5 dark:ring-slate-600 border border-gray-300 dark:border-slate-600 focus:outline-none">
                      <button
                        onClick={handleLogout}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700"
                      >
                        Sign Out
                      </button>
                      <button
                        onClick={toggleSecurityDropdown}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700"
                      >
                        Security Timeout
                      </button>

                      <Link
                        to="/terms"
                        onClick={() => setShowUserDropdown(false)}
                        className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700"
                      >
                        Terms of Service
                      </Link>

                      {/* Affiliate — shown to EVERY signed-in user.
                          The label is the only thing that changes: an existing
                          affiliate goes to their dashboard, everyone else lands
                          on the page explaining the program, where they can
                          apply. Same URL for both. */}
                      <Link
                        to="/affiliate"
                        onClick={() => setShowUserDropdown(false)}
                        className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700"
                      >
                        {isAffiliate ? 'Affiliate Dashboard' : 'Affiliate Program'}
                      </Link>

                      {/* Account link - visible to all users */}
                      <Link
                        to="/account"
                        onClick={() => setShowUserDropdown(false)}
                        className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700"
                      >
                        Account Settings
                      </Link>
                      
                      {userCompanies.length > 0 && (
                        <div className="px-4 py-2 border-t border-gray-200 dark:border-slate-600">
                          <label htmlFor="dropdown-company-select" className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                            Company
                          </label>
                          <select
                            id="dropdown-company-select"
                            value={selectedCompany?.companyId || ''}
                            onChange={(e) => {
                              changeSelectedCompany(Number(e.target.value));
                              setShowUserDropdown(false);
                            }}
                            className="block w-full pl-2 pr-8 py-1 text-xs border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-primary-500 focus:border-primary-500 rounded-md"
                            aria-label="Select company"
                          >
                            {userCompanies.map((company) => (
                              <option key={company.companyId} value={company.companyId}>
                                {company.companyName}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>
                  )}

                  {showSecurityDropdown && (
                    <div 
                      ref={securityDropdownRef}
                      className="origin-top-right absolute right-0 mt-2 w-64 rounded-md shadow-lg py-3 bg-white dark:bg-slate-800 ring-1 ring-black ring-opacity-5 dark:ring-slate-600 border border-gray-300 dark:border-slate-600"
                    >
                      <div className="px-4 py-2">
                        <div className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-1">Automatic Timeout</div>
                        <div className="flex items-center">
                          <div className="bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 px-3 py-2 rounded-md text-sm flex items-center justify-between w-full">
                            <span>
                              {currentUser?.lessThanDayLogout
                                ? `${Math.floor(currentUser.automaticLogoutDurationMinutes / 60)} hours`
                                : `${Math.floor(currentUser.automaticLogoutDurationMinutes / (60 * 24))} days`}
                            </span>
                            <button
                              onClick={toggleEditLogoutForm}
                              className="ml-2 text-primary-600 dark:text-primary-400 underline hover:text-primary-800 dark:hover:text-primary-200 text-xs font-medium"
                            >
                              Edit
                            </button>
                          </div>
                        </div>
                      </div>
                      <div className="flex justify-end px-4 mt-2">
                        <button
                          onClick={closeSecurityDropdown}
                          className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 px-3 py-1 rounded-md hover:bg-gray-100 dark:hover:bg-slate-700"
                        >
                          Close
                        </button>
                      </div>
                    </div>
                  )}

                  {showEditLogoutForm && (
                    <div 
                      ref={editLogoutFormRef}
                      className="origin-top-right absolute right-0 mt-2 w-72 rounded-md shadow-lg bg-white dark:bg-slate-800 ring-1 ring-black ring-opacity-5 dark:ring-slate-600 border border-gray-300 dark:border-slate-600 p-4"
                    >
                      <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">Edit Security Timeout</h3>
                      <div className="mb-3">
                        <label className="inline-flex items-center">
                          <input
                            type="checkbox"
                            checked={lessThanDayLogout}
                            onChange={() => setLessThanDayLogout(!lessThanDayLogout)}
                            className="form-checkbox h-4 w-4 text-primary-600 dark:text-primary-500 rounded border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700"
                          />
                          <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Use hours instead of days</span>
                        </label>
                      </div>

                      {lessThanDayLogout ? (
                        <div className="mb-3">
                          <label htmlFor="logoutHours" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Hours
                          </label>
                          <input
                            type="number"
                            id="logoutHours"
                            min="1"
                            max="23"
                            value={logoutHours}
                            onChange={(e) => setLogoutHours(parseInt(e.target.value, 10) || 0)}
                            className="mt-1 block w-full border border-gray-300 dark:border-slate-600 rounded-md shadow-sm py-2 px-3 bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                          />
                          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Minimum: 1 hour, Maximum: 23 hours</p>
                        </div>
                      ) : (
                        <div className="mb-3">
                          <label htmlFor="logoutDays" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Days
                          </label>
                          <input
                            type="number"
                            id="logoutDays"
                            min="1"
                            max="30"
                            value={logoutDays}
                            onChange={(e) => setLogoutDays(parseInt(e.target.value, 10) || 0)}
                            className="mt-1 block w-full border border-gray-300 dark:border-slate-600 rounded-md shadow-sm py-2 px-3 bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                          />
                          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Minimum: 1 day, Maximum: 30 days</p>
                        </div>
                      )}

                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => setShowEditLogoutForm(false)}
                          className="px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-slate-700 hover:bg-gray-50 dark:hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleSaveLogoutSettings}
                          className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                        >
                          Save
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Theme toggle - Desktop */}
            <ThemeToggle />

            {/* Sign In button */}
            {!isAuthenticated && (
              <button
                onClick={toggleLoginForm}
                className="px-3 py-2 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
              >
                Sign In
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div ref={mobileMenuRef} className="sm:hidden absolute w-full bg-white dark:bg-slate-800 shadow-lg dark:shadow-slate-900/30 z-20 border-t border-gray-200 dark:border-slate-700">
          <div className="pt-2 pb-3 space-y-1">
            <div className="flex justify-between items-center px-4 py-2 border-b border-gray-200 dark:border-slate-700">
              <div className="text-base font-medium text-gray-800 dark:text-gray-200">Menu</div>
              <button 
                onClick={() => setMobileMenuOpen(false)} 
                className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 p-2"
                aria-label="Close menu"
              >
                <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Public links */}
            <Link
              to="/pricing"
              className="block px-4 py-3 text-base font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700"
              onClick={() => setMobileMenuOpen(false)}
            >
              Pricing
            </Link>

            <Link
              to="/about"
              className="block px-4 py-3 text-base font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700"
              onClick={() => setMobileMenuOpen(false)}
            >
              About
            </Link>

            <Link
              to="/terms"
              className="block px-4 py-3 text-base font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700"
              onClick={() => setMobileMenuOpen(false)}
            >
              Terms of Service
            </Link>

            <Link
              to="/privacy"
              className="block px-4 py-3 text-base font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700"
              onClick={() => setMobileMenuOpen(false)}
            >
              Privacy Policy
            </Link>
            
            {/* Authenticated user links */}
            {isAuthenticated && (
              <>
                <div className="border-t border-gray-200 dark:border-slate-700 my-2"></div>
                
                <Link
                  to="/orders"
                  className="block px-4 py-3 text-base font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Orders
                </Link>

                <Link
                  to="/admin"
                  className="block px-4 py-3 text-base font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Admin
                </Link>

                {/* Affiliate — see the note on the desktop entry. */}
                <Link
                  to="/affiliate"
                  className="block px-4 py-3 text-base font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {isAffiliate ? 'Affiliate Dashboard' : 'Affiliate Program'}
                </Link>

                {/* Account link - visible to all authenticated users */}
                <Link
                  to="/account"
                  className="block px-4 py-3 text-base font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Account Settings
                </Link>

                {/* Super Admin section for mobile */}
                {(isSuperAdmin() || isImpersonating) && (
                  <div className="border-t border-gray-200 dark:border-slate-700 mt-2 pt-2">
                    <div className="px-4 py-2 text-sm font-medium text-purple-700 dark:text-purple-400 flex items-center">
                      Super Admin
                      {isImpersonating && (
                        <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-orange-100 dark:bg-orange-900/50 text-orange-800 dark:text-orange-300">
                          Impersonating
                        </span>
                      )}
                    </div>
                    <Link
                      to="/super-admin/dashboard"
                      className="block px-6 py-2 text-base font-medium text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/30"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Dashboard
                    </Link>
                    <Link
                      to="/super-admin/plans"
                      className="block px-6 py-2 text-base font-medium text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/30"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Manage Plans
                    </Link>
                    <Link
                      to="/super-admin/companies"
                      className="block px-6 py-2 text-base font-medium text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/30"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Manage Companies
                    </Link>
                    <Link
                      to="/super-admin/members"
                      className="block px-6 py-2 text-base font-medium text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/30"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Manage Members
                    </Link>
                    <Link
                      to="/super-admin/settings"
                      className="block px-6 py-2 text-base font-medium text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/30"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      App Settings
                    </Link>
                    {/* Cabinet drawings authoring (ecard model) */}
                    <Link
                      to="/super-admin/cabinet-drawings"
                      className="block px-6 py-2 text-base font-medium text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/30"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Cabinet Drawings
                    </Link>
                    <Link
                      to="/super-admin/item-drawings"
                      className="block px-6 py-2 text-base font-medium text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/30"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Item Drawings
                    </Link>
                    <Link
                      to="/super-admin/room-drawings"
                      className="block px-6 py-2 text-base font-medium text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/30"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Room Drawings
                    </Link>
                    <Link
                      to="/super-admin/image-library"
                      className="block px-6 py-2 text-base font-medium text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/30"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Image Library
                    </Link>
                    <Link
                      to="/super-admin/construction-profiles"
                      className="block px-6 py-2 text-base font-medium text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/30"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Construction Profiles
                    </Link>
                    <Link
                      to="/super-admin/engines"
                      className="block px-6 py-2 text-base font-medium text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/30"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Engine Parts
                    </Link>
                    <Link
                      to="/super-admin/impersonation"
                      className="block px-6 py-2 text-base font-medium text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/30"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Impersonation
                    </Link>
                  </div>
                )}

                <div className="border-t border-gray-200 dark:border-slate-700 my-2"></div>

                <button
                  onClick={() => {
                    handleLogout();
                    setMobileMenuOpen(false);
                  }}
                  className="block w-full text-left px-4 py-3 text-base font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700"
                >
                  Sign Out
                </button>

                <div className="px-4 py-3">
                  <div className="text-base font-medium text-gray-700 dark:text-gray-200 mb-2">Security Timeout</div>
                  <div className="bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 px-3 py-2 rounded-md text-sm flex items-center justify-between">
                    <span>
                      {currentUser?.lessThanDayLogout
                        ? `${Math.floor(currentUser.automaticLogoutDurationMinutes / 60)} hours`
                        : `${Math.floor(currentUser.automaticLogoutDurationMinutes / (60 * 24))} days`}
                    </span>
                    <button
                      onClick={() => {
                        setMobileMenuOpen(false);
                        setTimeout(() => {
                          setShowEditLogoutForm(true);
                        }, 100);
                      }}
                      className="ml-2 text-primary-600 dark:text-primary-400 underline hover:text-primary-800 dark:hover:text-primary-200 text-xs font-medium"
                    >
                      Edit
                    </button>
                  </div>
                </div>

                <div className="px-4 py-2 border-t border-gray-200 dark:border-slate-700">
                  <Link
                    to="/terms"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block py-2 text-base font-medium text-gray-700 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white"
                  >
                    Terms of Service
                  </Link>
                </div>

                {userCompanies.length > 0 && (
                  <div className="px-4 py-3 border-t border-gray-200 dark:border-slate-700">
                    <label htmlFor="mobile-company-select" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Company
                    </label>
                    <select
                      id="mobile-company-select"
                      value={selectedCompany?.companyId || ''}
                      onChange={(e) => {
                        changeSelectedCompany(Number(e.target.value));
                        setMobileMenuOpen(false);
                      }}
                      className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-primary-500 focus:border-primary-500 rounded-md"
                    >
                      {userCompanies.map((company) => (
                        <option key={company.companyId} value={company.companyId}>
                          {company.companyName}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </>
            )}

            {/* Sign In button */}
            {!isAuthenticated && (
              <button
                onClick={() => {
                  toggleLoginForm();
                  setMobileMenuOpen(false);
                }}
                className="block w-full text-left px-4 py-3 text-base font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700"
              >
                Sign In
              </button>
            )}
          </div>
        </div>
      )}

      {/* Mobile Edit Logout Form Modal */}
      {showEditLogoutForm && !showSecurityDropdown && (
        <div className="fixed inset-0 bg-gray-600/75 dark:bg-slate-900/80 flex items-center justify-center z-50 sm:hidden">
          <div className="bg-white dark:bg-slate-800 rounded-lg w-full mx-4 max-w-md overflow-hidden shadow-xl border border-gray-300 dark:border-slate-600">
            <div className="flex justify-between items-center px-4 py-3 border-b border-gray-200 dark:border-slate-700">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Edit Security Timeout</h3>
              <button 
                onClick={() => setShowEditLogoutForm(false)}
                className="text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-300"
              >
                <span className="sr-only">Close</span>
                <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="px-4 py-4">
              <div className="mb-4">
                <label className="inline-flex items-center">
                  <input
                    type="checkbox"
                    checked={lessThanDayLogout}
                    onChange={() => setLessThanDayLogout(!lessThanDayLogout)}
                    className="form-checkbox h-5 w-5 text-primary-600 dark:text-primary-500 rounded border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700"
                  />
                  <span className="ml-2 text-gray-700 dark:text-gray-300">Use hours instead of days</span>
                </label>
              </div>

              {lessThanDayLogout ? (
                <div className="mb-4">
                  <label htmlFor="mobileLogoutHours" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Hours
                  </label>
                  <input
                    type="number"
                    id="mobileLogoutHours"
                    min="1"
                    max="23"
                    value={logoutHours}
                    onChange={(e) => setLogoutHours(parseInt(e.target.value, 10) || 0)}
                    className="mt-1 block w-full border border-gray-300 dark:border-slate-600 rounded-md shadow-sm py-2 px-3 bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-base"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Minimum: 1 hour, Maximum: 23 hours</p>
                </div>
              ) : (
                <div className="mb-4">
                  <label htmlFor="mobileLogoutDays" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Days
                  </label>
                  <input
                    type="number"
                    id="mobileLogoutDays"
                    min="1"
                    max="30"
                    value={logoutDays}
                    onChange={(e) => setLogoutDays(parseInt(e.target.value, 10) || 0)}
                    className="mt-1 block w-full border border-gray-300 dark:border-slate-600 rounded-md shadow-sm py-2 px-3 bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-base"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Minimum: 1 day, Maximum: 30 days</p>
                </div>
              )}
            </div>
            
            <div className="px-4 py-3 bg-gray-50 dark:bg-slate-700/50 flex justify-end space-x-3">
              <button
                onClick={() => setShowEditLogoutForm(false)}
                className="px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-slate-700 hover:bg-gray-50 dark:hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveLogoutSettings}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Login Form Modal */}
      {showLoginForm && (
        <div className="fixed inset-0 bg-gray-600/50 dark:bg-slate-900/70 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl overflow-hidden max-w-md w-full m-4">
            <div className="p-6">
              <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-gray-100">Sign In</h2>
              <LoginForm onClose={toggleLoginForm} />
              <div className="mt-4 text-center">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Don't have an account?{' '}
                  <button
                    onClick={() => {
                      setShowLoginForm(false);
                      navigate('/register');
                    }}
                    className="text-primary-600 dark:text-primary-400 hover:text-primary-500 dark:hover:text-primary-300 font-medium"
                  >
                    Sign Up
                  </button>
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;